import { getMonthlyBillAmount } from '@fruitful/fruitils';

import { requestBuilder } from '../../../http/RequestBuilder';
import type { Bill } from '../../entityTypes';

export const getMembershipBillsQueryKey = ({
    membershipId,
}: {
    membershipId?: string | null;
}): string => `getMembershipBills/${membershipId ?? ''}`;

/**
 * Bills query for Bills- Dev Tools: `includeInactive` + `includeTransactions`
 * (see admin GET .../bills).
 */
export const getMembershipBillsIncludingInactiveQueryKey = ({
    membershipId,
}: {
    membershipId?: string | null;
}): string =>
    `${getMembershipBillsQueryKey({ membershipId })}/includingInactiveWithTransactions`;

const useGetBills = requestBuilder.query<Bill[], { membershipId: string }>({
    endpoint: ({ membershipId }) =>
        `/api/admin/memberships/${membershipId}/bills`,
    queryKey: ({ membershipId }) => `getMembershipBills/${membershipId}`,
});

const useGetBillsIncludingInactive = requestBuilder.query<
    Bill[],
    { membershipId: string }
>({
    endpoint: ({ membershipId }) =>
        `/api/admin/memberships/${membershipId}/bills?includeInactive=true&includeTransactions=true`,
    queryKey: ({ membershipId }) =>
        getMembershipBillsIncludingInactiveQueryKey({ membershipId }),
});

interface MembershipBills {
    membershipId: string | null;
    bills: Bill[] | undefined;
}

export const useMembershipBills = (
    membershipId: string | null,
): {
    membershipBills: MembershipBills;
    isLoadingBills: boolean;
    isBillsError: boolean;
} => {
    const {
        data: bills,
        isLoading,
        isError,
        error,
    } = useGetBills(
        {
            membershipId: membershipId ?? '',
        },
        {
            enabled: membershipId != null,
            select: (data) => {
                return augmentFetchedMembershipBills(data ?? []);
            },
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 60, // 1 hour
        },
    );

    if (isError) {
        console.error('useMembershipBills error:', error);
        console.error('useMembershipBills error details:', {
            membershipId,
            error: error?.message,
            errorStack: error?.stack,
        });
    }

    return {
        membershipBills: { bills, membershipId },
        isLoadingBills: isLoading,
        isBillsError: isError,
    };
};

export const useMembershipBillsIncludingInactive = (
    membershipId: string | null,
): {
    membershipBills: MembershipBills;
    isLoadingBills: boolean;
    isBillsError: boolean;
} => {
    const {
        data: bills,
        isLoading,
        isError,
        error,
    } = useGetBillsIncludingInactive(
        {
            membershipId: membershipId ?? '',
        },
        {
            enabled: membershipId != null,
            select: (data) => {
                return augmentFetchedMembershipBills(data ?? []);
            },
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 60,
        },
    );

    if (isError) {
        console.error('useMembershipBillsIncludingInactive error:', error);
        console.error('useMembershipBillsIncludingInactive error details:', {
            membershipId,
            error: error?.message,
            errorStack: error?.stack,
        });
    }

    return {
        membershipBills: { bills, membershipId },
        isLoadingBills: isLoading,
        isBillsError: isError,
    };
};

const augmentFetchedMembershipBills = (bills: Bill[]) => {
    return bills.map((bill) => {
        return {
            ...bill,
            monthlyAmount: getMonthlyBillAmount(bill.amount, bill.cadence),
        };
    });
};
